import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Monitor, Laptop, Download, CheckCircle2, Mic, Zap, MessageSquare,
    Users, Package, FileText, Mail, Sparkles, Clock, Shield, RefreshCw,
    Loader2, ExternalLink, ChevronDown, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURE_ICONS = {
    0: Clock,       // system tray
    1: Mic,         // transcription
    2: MessageSquare, // playbook
    3: Sparkles,    // answer detection
    4: Users,       // DISC
    5: Package,     // product recs
    6: FileText,    // summaries
    7: Mail,        // follow-up email
    8: Zap,         // CRM records
};

function PlatformCard({ platform, downloads, requirements, onDownload, version, beta }) {
    const [expanded, setExpanded] = useState(false);
    const isMac = platform === 'mac';
    const Icon = isMac ? Laptop : Monitor;
    const title = isMac ? 'macOS' : 'Windows';
    const formats = Object.entries(downloads || {});
    const primary = formats[0];

    return (
        <div className="bg-white border border-stone-200 rounded-2xl p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                    isMac ? 'bg-stone-900' : 'bg-blue-600'
                }`}>
                    <Icon className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <h3 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {title}
                        </h3>
                        {beta && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full uppercase tracking-wide">
                                Beta
                            </span>
                        )}
                    </div>
                    {version && (
                        <p className="text-[11px] text-stone-400 mt-0.5">v{version}</p>
                    )}
                    <p className="text-xs text-stone-400 mt-0.5">{requirements}</p>
                </div>
            </div>

            {/* Primary download button */}
            {primary && (
                <Button
                    className="w-full mt-4 gap-2"
                    size="lg"
                    onClick={() => onDownload(primary[1].url, primary[1].label)}
                >
                    <Download className="w-4 h-4" />
                    Download {primary[1].label}
                    {primary[1].size_mb && (
                        <span className="text-xs opacity-70 ml-1">({primary[1].size_mb} MB)</span>
                    )}
                </Button>
            )}

            {/* Alternative formats */}
            {formats.length > 1 && (
                <div className="mt-2">
                    <button
                        onClick={() => setExpanded(!expanded)}
                        className="flex items-center gap-1 text-xs text-stone-400 hover:text-stone-600"
                    >
                        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        Other formats
                    </button>
                    {expanded && (
                        <div className="mt-2 space-y-1.5">
                            {formats.slice(1).map(([key, dl]) => (
                                <button
                                    key={key}
                                    onClick={() => onDownload(dl.url, dl.label)}
                                    className="w-full flex items-center justify-between px-3 py-2 bg-stone-50 rounded-lg text-xs text-stone-600 hover:bg-stone-100 transition-colors"
                                >
                                    <span>{dl.label}</span>
                                    <span className="flex items-center gap-1 text-stone-400">
                                        {dl.size_mb && `${dl.size_mb} MB`}
                                        <Download className="w-3 h-3" />
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function DesktopApp() {
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [userOS, setUserOS] = useState('unknown');

    useEffect(() => {
        // Detect user OS
        const ua = navigator.userAgent.toLowerCase();
        if (ua.includes('mac')) setUserOS('mac');
        else if (ua.includes('win')) setUserOS('windows');

        loadInfo();
    }, []);

    const loadInfo = async () => {
        try {
            const res = await axios.get(`${API}/public/desktop-info`);
            setInfo(res.data);
        } catch (err) {
            console.error('Failed to load desktop app info:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = (url, label) => {
        window.open(url, '_blank');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
        );
    }

    const features = info?.features || [];
    const downloads = info?.downloads || {};
    const requirements = info?.requirements || {};

    // Order platforms: user's OS first
    const platforms = userOS === 'windows'
        ? [['windows', downloads.windows], ['mac', downloads.mac]]
        : [['mac', downloads.mac], ['windows', downloads.windows]];

    return (
        <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
            {/* Hero */}
            <div className="text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-semibold mb-4">
                    <Sparkles className="w-3 h-3" /> Meeting Copilot
                </div>
                <div className="flex items-center justify-center gap-3">
                    <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {info?.app_name || 'NurtureCRM Meeting Copilot'}
                    </h1>
                    {info?.beta && (
                        <span className="px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full uppercase tracking-wide">
                            Beta
                        </span>
                    )}
                </div>
                <p className="text-stone-500 mt-2 max-w-lg mx-auto text-sm">
                    {info?.description || 'AI-powered meeting assistant for your sales calls.'}
                </p>
                {info?.version && (
                    <p className="text-xs text-stone-400 mt-1">Version {info.version}{info?.beta ? ' Beta' : ''}</p>
                )}
            </div>

            {/* Download Cards or Coming Soon */}
            {info?.coming_soon ? (
                <div className="bg-white border border-stone-200 rounded-2xl p-8 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-violet-50 flex items-center justify-center mx-auto mb-4">
                        <Monitor className="w-8 h-8 text-violet-600" />
                    </div>
                    <h2 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Coming Soon
                    </h2>
                    <p className="text-sm text-stone-500 mt-2 max-w-md mx-auto">
                        The desktop app is currently being built and will be available for download shortly.
                        Check back soon for macOS and Windows installers.
                    </p>
                    <div className="flex items-center justify-center gap-6 mt-6">
                        <div className="flex items-center gap-2 text-sm text-stone-400">
                            <Laptop className="w-5 h-5" />
                            <span>macOS</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-stone-400">
                            <Monitor className="w-5 h-5" />
                            <span>Windows</span>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {platforms.map(([platform, dl]) => dl && Object.keys(dl).length > 0 && (
                        <PlatformCard
                            key={platform}
                            platform={platform}
                            downloads={dl}
                            requirements={requirements[platform]}
                            onDownload={handleDownload}
                            version={info?.version}
                            beta={info?.beta}
                        />
                    ))}
                </div>
            )}

            {/* Beta Notice */}
            {info?.beta && !info?.coming_soon && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 flex items-start gap-3">
                    <Shield className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-xs font-semibold text-amber-800">Beta Release</p>
                        <p className="text-[11px] text-amber-600 mt-0.5">
                            This is an early access build. Some features may be incomplete and you may encounter issues.
                            On macOS, if you see a security warning on first launch, right-click the app and select "Open" to bypass it.
                            Please report any bugs to your admin.
                        </p>
                    </div>
                </div>
            )}

            {/* Quick Setup Steps */}
            <div className="bg-stone-50 rounded-2xl p-6">
                <h2 className="text-sm font-bold text-stone-800 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Getting Started
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[
                        { step: '1', title: 'Download & Install', desc: 'Download for your OS and run the installer' },
                        { step: '2', title: 'Sign In', desc: 'Click the tray icon and sign in with your NurtureCRM account' },
                        { step: '3', title: 'Grant Permissions', desc: 'Allow microphone and screen recording access' },
                        { step: '4', title: 'Start a Meeting', desc: 'Select a playbook and start recording your call' },
                    ].map((s) => (
                        <div key={s.step} className="flex items-start gap-3">
                            <div className="w-7 h-7 rounded-full bg-stone-900 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                                {s.step}
                            </div>
                            <div>
                                <p className="text-xs font-semibold text-stone-800">{s.title}</p>
                                <p className="text-[11px] text-stone-500 mt-0.5">{s.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Features */}
            <div>
                <h2 className="text-sm font-bold text-stone-800 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    What It Does
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {features.map((feature, i) => {
                        const Icon = FEATURE_ICONS[i] || CheckCircle2;
                        return (
                            <div key={i} className="flex items-start gap-3 bg-white border border-stone-100 rounded-xl p-4">
                                <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                                    <Icon className="w-4 h-4 text-violet-600" />
                                </div>
                                <p className="text-xs text-stone-600 leading-relaxed">{feature}</p>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* How It Works */}
            <div className="bg-white border border-stone-200 rounded-2xl p-6">
                <h2 className="text-sm font-bold text-stone-800 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    How It Works
                </h2>
                <div className="space-y-4">
                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                            <Clock className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-stone-800">Before the Meeting</p>
                            <p className="text-xs text-stone-500 mt-0.5">
                                The app generates a research brief with attendee DISC profiles, pre-filled playbook answers from CRM data,
                                and product recommendations. Everything the rep needs, ready before the call starts.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                            <Mic className="w-5 h-5 text-red-500" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-stone-800">During the Meeting</p>
                            <p className="text-xs text-stone-500 mt-0.5">
                                Records mic + system audio, transcribes in real-time, tracks playbook questions,
                                auto-detects answers, suggests follow-ups based on DISC profiles, and recommends
                                products when pain points are mentioned. A floating overlay keeps everything visible.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                            <Zap className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-stone-800">After the Meeting</p>
                            <p className="text-xs text-stone-500 mt-0.5">
                                AI generates a summary, action items, buying signals, objections analysis, deal stage recommendation,
                                attendee sentiment, and a DISC-adapted follow-up email. One click creates all CRM records.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Auto-updates + Security */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 bg-stone-50 rounded-xl p-4">
                    <RefreshCw className="w-5 h-5 text-stone-400 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-xs font-semibold text-stone-700">Automatic Updates</p>
                        <p className="text-[11px] text-stone-500 mt-0.5">
                            The app checks for updates on launch and every 4 hours. New features are delivered automatically.
                        </p>
                    </div>
                </div>
                <div className="flex items-start gap-3 bg-stone-50 rounded-xl p-4">
                    <Shield className="w-5 h-5 text-stone-400 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-xs font-semibold text-stone-700">Secure by Design</p>
                        <p className="text-[11px] text-stone-500 mt-0.5">
                            Auth tokens stored in your system keychain. Audio streamed directly to the backend, never stored locally.
                            OAuth 2.0 + PKCE authentication flow.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
